import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import api from '../../utils/api';
import { useAuth } from '../../App';
import {
  Archive, Download, Upload, CheckCircle2, Search,
  ChevronDown, ChevronRight, FileText, Calendar,
  ChevronLeft, Eye, Trash2, Loader2, X, Filter, Receipt
} from 'lucide-react';
import { toast } from 'sonner';
import { handleNameCorrections } from '../../utils/nameCorrections';

export default function InvoicePaymentArchivePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedBatch, setExpandedBatch] = useState(null);
  const [expandedBenef, setExpandedBenef] = useState({});
  const [pdfPreview, setPdfPreview] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [search, setSearch] = useState('');
  const [filterBeneficiary, setFilterBeneficiary] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [matching, setMatching] = useState(null);      // batchId in fase di abbinamento
  const [reconcile, setReconcile] = useState(null);    // {batchId, unmatched, invoices, matched_count}
  const [assign, setAssign] = useState({});            // page_id -> invoice_id

  const fetchBatches = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      p.append('year', year);
      if (search) p.append('search', search);
      if (filterBeneficiary) p.append('beneficiary', filterBeneficiary);
      const res = await api.get(`/invoice-payment-batches?${p}`);
      setBatches(res.data.batches || []);
    } catch { toast.error('Errore caricamento'); } finally { setLoading(false); }
  };

  useEffect(() => { fetchBatches(); }, [year]);

  const applyFilters = (e) => { e?.preventDefault(); fetchBatches(); };
  const resetFilters = () => {
    setSearch(''); setFilterBeneficiary('');
    setTimeout(fetchBatches, 50);
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Eliminare questo batch di pagamento fatture?')) return;
    try { await api.delete(`/invoice-payment-batches/${id}`); toast.success('Eliminato'); fetchBatches(); }
    catch { toast.error('Errore'); }
  };

  const handleMarkExecuted = async (id) => {
    try {
      const res = await api.post(`/invoice-payment-batches/${id}/mark-executed`);
      toast.success(res.data.message || 'Eseguito');
      fetchBatches();
    } catch { toast.error('Errore'); }
  };

  const handleUploadResponse = async (id, file) => {
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await api.post(`/invoice-payment-batches/${id}/upload-response`, fd);
      toast.success(res.data.message || 'Risposta caricata');
      await handleNameCorrections(res.data?.name_corrections);
      fetchBatches();
      // Avvia subito l'abbinamento automatico delle ricevute
      handleMatchReceipts(id);
    } catch { toast.error('Errore'); }
  };

  const handleMatchReceipts = async (id) => {
    setMatching(id);
    try {
      const res = await api.post(`/invoice-payment-batches/${id}/match-receipts`);
      const d = res.data || {};
      toast.success(`Abbinate ${d.matched_count}/${d.total} ricevute`);
      fetchBatches();
      if ((d.unmatched_count || 0) > 0) {
        setAssign({});
        setReconcile({ batchId: id, unmatched: d.unmatched || [], invoices: d.invoices || [], matched_count: d.matched_count || 0 });
      } else {
        toast.success('Tutte le ricevute sono state abbinate ✅');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore abbinamento ricevute');
    } finally { setMatching(null); }
  };

  const handleReconcile = async (pageId) => {
    const invoiceId = assign[pageId];
    if (!invoiceId) { toast.error('Seleziona una fattura'); return; }
    try {
      await api.post(`/invoice-payment-batches/${reconcile.batchId}/reconcile`, { page_id: pageId, invoice_id: invoiceId });
      toast.success('Ricevuta abbinata e regola appresa');
      setReconcile(prev => {
        const left = (prev.unmatched || []).filter(u => u.page_id !== pageId);
        if (left.length === 0) { fetchBatches(); return null; }
        return { ...prev, unmatched: left, matched_count: prev.matched_count + 1 };
      });
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore conciliazione'); }
  };

  const handlePreviewPage = async (batchId, pageId) => {
    try {
      const res = await api.get(`/invoice-payment-batches/${batchId}/receipt-page/${pageId}`, { responseType: 'blob' });
      const ct = res.headers['content-type'] || 'application/pdf';
      window.open(URL.createObjectURL(new Blob([res.data], { type: ct })), '_blank');
    } catch { toast.error('Errore anteprima ricevuta'); }
  };

  const handleDownload = async (id, type, fname) => {
    try {
      const res = await api.get(`/invoice-payment-batches/${id}/download/${type}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = fname || `file_${type}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Errore download'); }
  };

  const handleViewPdf = async (id) => {
    try {
      const res = await api.get(`/invoice-payment-batches/${id}/download/bank_response?inline=true`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setPdfPreview(url);
    } catch { toast.error('Errore visualizzazione'); }
  };

  const fmtEur = (v) => Number(v).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
  const fmtDate = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return d; }
  };

  const hasActiveFilters = search || filterBeneficiary;

  return (
    <Layout title="Archivio Pagamenti Fatture">
      <div className="space-y-4" data-testid="invoice-payment-archive-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <FileText className="h-5 w-5 text-emerald-600" /> Archivio Pagamenti Fatture
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">Storico dei pagamenti XML CBI generati per le fatture</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
              <button onClick={() => setYear(y => y - 1)} className="p-1.5 hover:bg-white rounded transition-colors" data-testid="year-prev"><ChevronLeft className="h-4 w-4" /></button>
              <span className="px-3 py-1 font-bold text-slate-800 min-w-[60px] text-center" data-testid="year-display">{year}</span>
              <button onClick={() => setYear(y => y + 1)} className="p-1.5 hover:bg-white rounded transition-colors" data-testid="year-next"><ChevronRight className="h-4 w-4" /></button>
            </div>
            <Button size="sm" variant={showFilters ? 'default' : 'outline'} onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters-btn">
              <Filter className="h-3.5 w-3.5 mr-1.5" /> Filtri {hasActiveFilters && <span className="ml-1 bg-red-500 text-white rounded-full w-4 h-4 text-[10px] flex items-center justify-center">!</span>}
            </Button>
          </div>
        </div>

        {/* Filter panel */}
        {showFilters && (
          <Card className="p-4">
            <form onSubmit={applyFilters} className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Ricerca globale</label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
                  <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca fattura, causale..." className="w-full pl-8 pr-3 py-2 border rounded-lg text-sm" data-testid="search-input" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Beneficiario</label>
                <input type="text" value={filterBeneficiary} onChange={(e) => setFilterBeneficiary(e.target.value)} placeholder="Nome beneficiario..." className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="beneficiary-input" />
              </div>
              <div className="flex items-end gap-2">
                <Button type="submit" size="sm" data-testid="apply-filters-btn"><Search className="h-3.5 w-3.5 mr-1" /> Cerca</Button>
                {hasActiveFilters && <Button type="button" size="sm" variant="outline" onClick={resetFilters} data-testid="reset-filters-btn"><X className="h-3.5 w-3.5 mr-1" /> Reset</Button>}
              </div>
            </form>
          </Card>
        )}

        {/* Batch list */}
        {loading ? (
          <div className="text-center py-12 text-slate-500"><Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />Caricamento...</div>
        ) : batches.length === 0 ? (
          <Card className="p-8 text-center text-slate-400"><Archive className="h-8 w-8 mx-auto mb-2" /><p>Nessun pagamento fatture trovato per {year}</p></Card>
        ) : (
          <div className="space-y-2">
            {batches.map(batch => {
              const isExp = expandedBatch === batch.id;
              const isExec = batch.status === 'executed';

              return (
                <Card key={batch.id} className="overflow-hidden" data-testid={`inv-batch-${batch.id}`}>
                  {/* Row header */}
                  <div className={`p-3 flex items-center gap-3 cursor-pointer hover:bg-slate-50 transition-colors ${isExp ? 'bg-slate-50 border-b' : ''}`}
                    onClick={() => setExpandedBatch(isExp ? null : batch.id)} data-testid={`inv-batch-header-${batch.id}`}>
                    {isExp ? <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />}

                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold shrink-0 ${isExec ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`} data-testid={`inv-batch-status-${batch.id}`}>
                      {isExec ? 'Pagato' : 'Generato'}
                    </span>

                    <div className="flex items-center gap-3 flex-wrap flex-1 min-w-0">
                      <span className="text-sm text-slate-600 flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{fmtDate(batch.created_at)}</span>
                      {batch.filtered ? (
                        <>
                          <span className="text-sm font-medium text-blue-700">{batch.filtered_transactions} su {batch.total_transactions} bonifici</span>
                          <span className="text-sm text-blue-600">{batch.filtered_beneficiaries} beneficiari</span>
                        </>
                      ) : (
                        <>
                          <span className="text-sm text-slate-600">{batch.total_transactions} bonifici</span>
                          <span className="text-sm text-slate-600">{batch.total_beneficiaries} beneficiari</span>
                        </>
                      )}
                      <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">Fatture</span>
                    </div>

                    <div className="text-right shrink-0">
                      {batch.filtered ? (
                        <>
                          <span className="text-lg font-bold text-blue-700">{fmtEur(batch.filtered_total)}</span>
                          <span className="text-xs text-slate-400 block">su {fmtEur(batch.total_amount)}</span>
                        </>
                      ) : (
                        <span className="text-lg font-bold text-slate-900">{fmtEur(batch.total_amount)}</span>
                      )}
                    </div>

                    {isAdmin && (
                      <button onClick={(e) => handleDelete(e, batch.id)} className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors shrink-0" title="Elimina" data-testid={`delete-batch-${batch.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  {/* Expanded content */}
                  {isExp && (
                    <div className="p-4 space-y-3">
                      {/* Actions bar */}
                      <div className="flex items-center gap-2 flex-wrap pb-3 border-b">
                        {isAdmin && (
                          <Button size="sm" variant="outline" onClick={() => handleDownload(batch.id, 'cbi', batch.cbi_filename)} data-testid={`download-xml-${batch.id}`}>
                            <Download className="h-3.5 w-3.5 mr-1" />XML CBI
                          </Button>
                        )}
                        {batch.bank_response_filename && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => handleViewPdf(batch.id)} data-testid={`view-pdf-${batch.id}`}><Eye className="h-3.5 w-3.5 mr-1" />Visualizza Risposta</Button>
                            <Button size="sm" variant="outline" onClick={() => handleDownload(batch.id, 'bank_response', batch.bank_response_filename)} data-testid={`download-pdf-${batch.id}`}><Download className="h-3.5 w-3.5 mr-1" />Scarica Risposta</Button>
                            <Button size="sm" variant="outline" className="border-teal-300 text-teal-700 hover:bg-teal-50" disabled={matching === batch.id} onClick={() => handleMatchReceipts(batch.id)} data-testid={`match-receipts-${batch.id}`}>
                              {matching === batch.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Receipt className="h-3.5 w-3.5 mr-1" />}
                              Abbina ricevute (AI)
                            </Button>
                          </>
                        )}
                        {!isExec && (
                          <>
                            <label className="cursor-pointer" data-testid={`upload-response-${batch.id}`}>
                              <input type="file" className="hidden" accept=".pdf,.zip" onChange={(e) => e.target.files[0] && handleUploadResponse(batch.id, e.target.files[0])} />
                              <span className="inline-flex items-center px-3 py-1.5 text-sm border rounded-lg hover:bg-slate-50 transition-colors">
                                <Upload className="h-3.5 w-3.5 mr-1" />Carica PDF Banca
                              </span>
                            </label>
                            <Button size="sm" onClick={() => handleMarkExecuted(batch.id)} className="bg-green-600 hover:bg-green-700" data-testid={`mark-executed-${batch.id}`}>
                              <CheckCircle2 className="h-3.5 w-3.5 mr-1" />Segna Pagato
                            </Button>
                          </>
                        )}
                        <span className="ml-auto text-xs text-slate-400">Da: {batch.created_by_name || batch.created_by}</span>
                      </div>

                      {/* Beneficiaries table */}
                      <div className="max-h-[500px] overflow-y-auto border rounded-lg">
                        <table className="w-full text-sm" data-testid="beneficiaries-table">
                          <thead className="bg-slate-50 sticky top-0 z-10"><tr>
                            <th className="text-left p-2 w-8"></th>
                            <th className="text-left p-2 font-medium text-slate-600">Beneficiario</th>
                            <th className="text-left p-2 font-medium text-slate-600">IBAN</th>
                            <th className="text-right p-2 font-medium text-slate-600">N.</th>
                            <th className="text-right p-2 font-medium text-slate-600">Totale</th>
                          </tr></thead>
                          <tbody className="divide-y">
                            {(batch.beneficiaries || []).map((ben, i) => {
                              const bk = `${batch.id}_${ben.name}`;
                              const bo = expandedBenef[bk];
                              return (
                                <React.Fragment key={i}>
                                  <tr className={`cursor-pointer transition-colors ${bo ? 'bg-blue-50' : 'hover:bg-slate-50'}`} onClick={() => setExpandedBenef(p => ({ ...p, [bk]: !bo }))}>
                                    <td className="p-2">{bo ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}</td>
                                    <td className="p-2 font-medium">{ben.name}</td>
                                    <td className="p-2 text-slate-500 font-mono text-xs">{ben.iban}</td>
                                    <td className="p-2 text-right">{ben.count}</td>
                                    <td className="p-2 text-right font-semibold">{fmtEur(ben.total)}</td>
                                  </tr>
                                  {bo && ben.transactions && (
                                    <tr><td colSpan={5} className="p-0"><div className="bg-slate-50 border-t border-b">
                                      <table className="w-full text-xs"><thead><tr className="text-slate-500">
                                        <th className="pl-10 pr-2 py-1.5 text-left font-medium">Riferimento</th>
                                        <th className="px-2 py-1.5 text-left font-medium">Causale</th>
                                        <th className="px-2 py-1.5 text-right font-medium pr-3">Importo</th>
                                      </tr></thead><tbody className="divide-y divide-slate-100">
                                        {ben.transactions.map((tx, j) => (
                                          <tr key={j} className="hover:bg-slate-100/50">
                                            <td className="pl-10 pr-2 py-1.5 font-mono text-slate-700">{tx.reference || '-'}</td>
                                            <td className="px-2 py-1.5 text-slate-600 truncate max-w-[300px]" title={tx.causale}>{tx.causale || '-'}</td>
                                            <td className="px-2 py-1.5 text-right font-medium text-slate-800 pr-3">{fmtEur(tx.amount)}</td>
                                          </tr>
                                        ))}
                                      </tbody></table>
                                    </div></td></tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        {/* PDF Preview Modal */}
        {pdfPreview && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => { URL.revokeObjectURL(pdfPreview); setPdfPreview(null); }}>
            <div className="bg-white rounded-xl w-full max-w-4xl h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-3 border-b">
                <h3 className="font-semibold text-slate-900">Risposta Banca</h3>
                <button onClick={() => { URL.revokeObjectURL(pdfPreview); setPdfPreview(null); }} className="p-1 hover:bg-slate-100 rounded" data-testid="close-pdf-preview"><X className="h-5 w-5" /></button>
              </div>
              <iframe src={pdfPreview} className="flex-1 w-full" title="Risposta Banca" />
            </div>
          </div>
        )}

        {/* Reconciliation Modal */}
        {reconcile && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="reconcile-modal">
            <div className="bg-white rounded-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
              <div className="flex items-center justify-between p-4 border-b">
                <div>
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2"><Receipt className="h-5 w-5 text-teal-600" />Conciliazione ricevute</h3>
                  <p className="text-xs text-slate-500 mt-0.5">{reconcile.matched_count} abbinate in automatico · {reconcile.unmatched.length} da assegnare a mano</p>
                </div>
                <button onClick={() => { setReconcile(null); fetchBatches(); }} className="p-1 hover:bg-slate-100 rounded" data-testid="close-reconcile"><X className="h-5 w-5" /></button>
              </div>
              <div className="p-4 overflow-y-auto space-y-3">
                {reconcile.unmatched.length === 0 ? (
                  <p className="text-center text-emerald-600 py-6">Tutte le ricevute sono state abbinate ✅</p>
                ) : reconcile.unmatched.map((u) => (
                  <div key={u.page_id} className="border rounded-lg p-3" data-testid={`unmatched-${u.page_id}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="text-sm">
                        <span className="font-medium text-slate-800">{u.issuer || 'Emittente non letto'}</span>
                        <span className="text-slate-500 ml-2">{u.amount != null ? fmtEur(u.amount) : 'importo n/d'}</span>
                        <span className="block text-xs text-amber-600">{u.reason}</span>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => handlePreviewPage(reconcile.batchId, u.page_id)} data-testid={`preview-page-${u.page_id}`}><Eye className="h-3.5 w-3.5 mr-1" />Anteprima</Button>
                    </div>
                    <div className="flex items-center gap-2">
                      <select className="flex-1 border rounded-lg px-2 py-1.5 text-sm" value={assign[u.page_id] || ''} onChange={(e) => setAssign(p => ({ ...p, [u.page_id]: e.target.value }))} data-testid={`assign-select-${u.page_id}`}>
                        <option value="">— Scegli fattura —</option>
                        {reconcile.invoices.filter(i => i.invoice_id).map(i => (
                          <option key={i.invoice_id} value={i.invoice_id}>{i.supplier} · {fmtEur(i.amount)} · {i.invoice_number}</option>
                        ))}
                      </select>
                      <Button size="sm" className="bg-teal-600 hover:bg-teal-700" onClick={() => handleReconcile(u.page_id)} data-testid={`reconcile-btn-${u.page_id}`}>Concilia</Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
